import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Search } from "lucide-react";
import { api } from "../lib/api";

const CONFIGS = {
  hours: {
    title: "Total Hours · Breakdown",
    endpoint: "/overview/breakdown/hours",
    cols: [
      { key: "job",   label: "Job",   align: "left",  mono: true,  color: "#00D4FF" },
      { key: "phase", label: "Phase", align: "left" },
      { key: "hours", label: "Hours", align: "right", mono: true, fmt: (v) => v?.toLocaleString() },
    ],
    totalKey: "hours",
    totalFmt: (v) => `${v.toLocaleString()} hrs`,
    searchKeys: ["job", "phase"],
  },
  production: {
    title: "Production SF · Breakdown",
    endpoint: "/overview/breakdown/production",
    cols: [
      { key: "job",    label: "Job",    align: "left",  mono: true, color: "#00D4FF" },
      { key: "plant",  label: "Plant",  align: "left" },
      { key: "pieces", label: "Pieces", align: "right", mono: true },
      { key: "sf",     label: "Total SF", align: "right", mono: true, fmt: (v) => v?.toLocaleString() },
    ],
    totalKey: "sf",
    totalFmt: (v) => `${v.toLocaleString()} SF`,
    searchKeys: ["job", "plant"],
  },
  materials: {
    title: "Material Cost · Breakdown",
    endpoint: "/overview/breakdown/materials",
    cols: [
      { key: "job",   label: "Job",   align: "left",  mono: true, color: "#00D4FF" },
      { key: "phase", label: "Phase", align: "left" },
      { key: "cost",  label: "Cost",  align: "right", mono: true, fmt: (v) => `$${v?.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}` },
    ],
    totalKey: "cost",
    totalFmt: (v) => `$${v.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`,
    searchKeys: ["job", "phase"],
  },
};

export default function KpiDrillModal({ kind, open, onClose, filter, department, titleOverride }) {
  const cfg = CONFIGS[kind];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open || !cfg) return;
    setLoading(true);
    const params = { preset: filter?.preset || "all" };
    if (filter?.preset === "custom") { params.start = filter.start; params.end = filter.end; }
    if (filter?.include_mexico !== undefined) params.include_mexico = filter.include_mexico;
    if (filter?.show_mexico !== undefined) params.show_mexico = filter.show_mexico;
    if (department) params.department = department;
    api.get(cfg.endpoint, { params })
       .then((r) => setRows(r.data))
       .finally(() => setLoading(false));
  }, [open, kind, filter, department]);

  if (!cfg) return null;

  const filtered = rows.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return cfg.searchKeys.some((k) => String(r[k] || "").toLowerCase().includes(q));
  });
  const total = filtered.reduce((s, r) => s + (r[cfg.totalKey] || 0), 0);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid={`kpi-drill-${kind}`}
        className="bg-[#12141D] border-[#00D4FF]/30 max-w-4xl rounded-none p-0 text-white"
      >
        <DialogHeader className="p-5 border-b border-border">
          <DialogTitle className="font-heading text-lg tracking-tight flex items-center justify-between">
            <span>{titleOverride || cfg.title}</span>
            <span className="text-xs font-mono font-normal text-muted-foreground">
              {filtered.length} rows · {cfg.totalFmt(total)}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="p-5 border-b border-border">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="kpi-drill-search"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-[#0A0B10] border-border text-white rounded-none focus-visible:ring-[#00D4FF]"
            />
          </div>
        </div>

        <div className="max-h-[55vh] overflow-auto">
          <table className="w-full text-left">
            <thead className="text-[10px] uppercase tracking-widest text-[#94A3B8] bg-[#1A1C28] sticky top-0">
              <tr>
                {cfg.cols.map((c) => (
                  <th key={c.key} className={`py-3 px-4 ${c.align === "right" ? "text-right" : ""}`}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={cfg.cols.length} className="py-8 text-center text-xs uppercase tracking-widest text-muted-foreground">Loading…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={cfg.cols.length} className="py-8 text-center text-xs uppercase tracking-widest text-muted-foreground">No rows</td></tr>}
              {!loading && filtered.map((r, i) => (
                <tr key={i} className="border-b border-border hover:bg-[#1A1C28]">
                  {cfg.cols.map((c) => {
                    const v = r[c.key];
                    const display = c.fmt ? c.fmt(v) : (v ?? "—");
                    return (
                      <td key={c.key}
                        className={`py-3 px-4 text-sm ${c.align === "right" ? "text-right" : ""} ${c.mono ? "font-mono" : ""}`}
                        style={c.color ? { color: c.color } : {}}>
                        {display}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
